import { useState, useEffect, useMemo } from "react";
import { fetchProducts } from "../services/api";
import ProductCard from "../components/ProductCard";
import { colorToCss } from "../utils/colors";
import styles from "./Products.module.css";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [activeColor, setActiveColor] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => { loadProducts(search); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  async function loadProducts(query) {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProducts(query);
      setProducts(data.results || data);
    } catch {
      setError("We couldn't load the collection right now. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  }

  // Build category chips from whatever categories the catalog returns
  const categories = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      if (p.category) map.set(p.category, p.category_display || p.category);
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [products]);

  // Colors available within the current category selection
  const colors = useMemo(() => {
    const base = activeCat === "all" ? products : products.filter((p) => p.category === activeCat);
    return Array.from(new Set(base.map((p) => p.color).filter(Boolean))).sort();
  }, [products, activeCat]);

  const visible = products.filter(
    (p) =>
      (activeCat === "all" || p.category === activeCat) &&
      (activeColor === "all" || p.color === activeColor)
  );

  return (
    <div className={styles.page}>
      <div className={styles.banner}>
        <div className="container">
          <span className="eyebrow" style={{ color: "var(--accent)" }}>The Collection</span>
          <h1 className={styles.bannerTitle}>Formal Wear, Curated</h1>
          <p className={styles.bannerSub}>
            Suits, gowns, and refined essentials — each piece selected for cut, cloth, and craft.
          </p>
        </div>
      </div>

      <div className="container">
        <div className={styles.toolbar}>
          <div className={styles.chips}>
            <button
              className={`${styles.chip} ${activeCat === "all" ? styles.chipActive : ""}`}
              onClick={() => { setActiveCat("all"); setActiveColor("all"); }}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.value}
                className={`${styles.chip} ${activeCat === c.value ? styles.chipActive : ""}`}
                onClick={() => { setActiveCat(c.value); setActiveColor("all"); }}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className={styles.searchBox}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search the collection…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {colors.length > 1 && (
          <div className={styles.colorRow}>
            <span className={styles.colorRowLabel}>Color</span>
            <button
              className={`${styles.colorChip} ${activeColor === "all" ? styles.colorChipActive : ""}`}
              onClick={() => setActiveColor("all")}
            >
              All
            </button>
            {colors.map((c) => (
              <button
                key={c}
                className={`${styles.colorChip} ${activeColor === c ? styles.colorChipActive : ""}`}
                onClick={() => setActiveColor(c)}
              >
                <span className={styles.colorChipDot} style={{ background: colorToCss(c) }} />
                {c}
              </button>
            ))}
          </div>
        )}

        {loading && <p className={styles.statusMsg}>Curating the collection…</p>}
        {error && <p className={`${styles.statusMsg} ${styles.errorMsg}`}>{error}</p>}

        {!loading && !error && visible.length === 0 && (
          <div className={styles.empty}><p>No pieces found{search ? ` for "${search}"` : ""}.</p></div>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className={styles.grid}>
            {visible.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg className="searchSvg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
